import { css, CSSObject } from '@emotion/react'
import {
  Color,
  Spacing,
  Theme,
  ThemedColors,
  ZIndex,
  gemVar,
} from '@opallabs/gem'

const rootClass = 'click-to-edit-input'
export const classes = {
  control: `${rootClass}-control`,
  editState: `${rootClass}-edit`,
  root: `${rootClass}-root`,
  viewState: `${rootClass}-view`,
} as const

export const editContainer = (theme: Theme): CSSObject => ({
  backgroundColor: 'transparent',
  borderColor: 'transparent',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  minBlockSize: 32,
  paddingInline: Spacing.XS,

  '&:not(:focus-within):hover': {
    backgroundColor: Color[theme].Surface.A000,
  },

  '&:focus-within': {
    backgroundColor: gemVar(ThemedColors.Surface1),
    borderColor: gemVar(ThemedColors.StrokeSecondary2),
  },
})

// Shared base styles for title button (single-line and multiline)
const viewBase: CSSObject = {
  border: 'none',
  fontSize: 'inherit',
  fontWeight: 'inherit',

  ':disabled': {
    cursor: 'default',
    opacity: 1,
  },
}

export const view: CSSObject = {
  ...viewBase,

  '.lapidary-button-contents': {
    display: 'block',
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
}

export const viewMultiline: CSSObject = {
  ...viewBase,
  alignItems: 'flex-start',
  // A button is a fixed 32px tall, and the edit state is only as tall as the
  // text it holds: without this the whole page below the title jumps up by the
  // difference the moment somebody clicks into it. `minBlockSize` cannot do
  // this — it is the button's `block-size` that has to go.
  blockSize: 'fit-content',
  lineHeight: 1.2,
  padding: 0,

  // Expand hover overlay to create padding effect
  '::before': {
    insetBlockStart: -2,
    insetBlockEnd: -2,
    insetInline: -6,
  },

  '.lapidary-button-contents': {
    display: 'block',
    overflow: 'visible',
    padding: 0,
    position: 'relative',
    textAlign: 'left',
    width: '100%',
    zIndex: ZIndex.Element.Level1,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  '.lapidary-button-text': {
    margin: 0,
  },
}

export const editInput: CSSObject = {
  fontSize: 'inherit',
  fontWeight: 'inherit',
  // match button padding to prevent text shift
  paddingInlineStart: 6,
}

export const editMultiline = css({
  backgroundColor: gemVar(ThemedColors.Surface1),
  borderColor: gemVar(ThemedColors.StrokeSecondary2),
  borderRadius: 4,
  borderWidth: 1,
  boxSizing: 'border-box',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 1.2,
  // Negative margins position border at same location as button's hover overlay
  marginBlockStart: -3,
  marginBlockEnd: -3,
  marginInline: -7,
  minHeight: 'auto',
  paddingBlockStart: 2,
  paddingBlockEnd: 2,
  paddingInline: 6,
  resize: 'none',
  width: 'calc(100% + 14px)',
})

export const editMultilineTheme = {
  light: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    horizontalPadding: 0,
    verticalPadding: 0,
  },
  dark: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    horizontalPadding: 0,
    verticalPadding: 0,
  },
}
